/**
 * ETA / congestion comparison helpers (portable).
 */

import { buildTimeDelta, maxspeedToMph } from './formatters'

/** Congestion levels that indicate real traffic (not free-flow). */
export const CONGESTED_LEVELS = new Set(['moderate', 'heavy', 'severe', 'high'])

const DELAY_KIND_RANK = { low: 0, moderate: 1, heavy: 2, severe: 3 }

export const DELAY_KIND_META = {
  low: {
    color: 'green',
    hex: '#3ecf8e',
    label: 'Low / no traffic',
    detail: 'Delay <10% and <3 min vs backend',
  },
  moderate: {
    color: 'yellow',
    hex: '#fbbf24',
    label: 'Moderate traffic',
    detail: 'Delay 10–30% or 3–10 min vs backend',
  },
  heavy: {
    color: 'orange',
    hex: '#f97316',
    label: 'Heavy traffic',
    detail: 'Delay 30–60% or 10–20 min vs backend',
  },
  severe: {
    color: 'red',
    hex: '#ff6b6b',
    label: 'Severe traffic',
    detail: 'Delay >60% or >20 min vs backend',
  },
}

export const TRAFFIC_LEGEND = [
  DELAY_KIND_META.low,
  DELAY_KIND_META.moderate,
  DELAY_KIND_META.heavy,
  DELAY_KIND_META.severe,
]

export const CONGESTION_COLORS = {
  unknown: '#3d8bfd', // blue — no congestion annotation
  low: '#3ecf8e', // green
  moderate: '#fbbf24', // amber
  heavy: '#f97316', // orange
  severe: '#ff6b6b', // red
  unavailable: '#d946ef', // fuchsia — geometry diverged
}

/** Legend for map congestion coloring (distinct from ETA delay legend). */
export const MAP_CONGESTION_LEGEND = [
  {
    color: 'unknown',
    hex: CONGESTION_COLORS.unknown,
    label: 'Unknown',
    detail: 'No congestion annotation',
  },
  {
    color: 'low',
    hex: CONGESTION_COLORS.low,
    label: 'Low',
    detail: 'Free-flowing traffic',
  },
  {
    color: 'moderate',
    hex: CONGESTION_COLORS.moderate,
    label: 'Moderate',
    detail: 'Some backup',
  },
  {
    color: 'heavy',
    hex: CONGESTION_COLORS.heavy,
    label: 'Heavy',
    detail: 'Significant backup',
  },
  {
    color: 'severe',
    hex: CONGESTION_COLORS.severe,
    label: 'Severe',
    detail: 'Traffic heavily backed up',
  },
  {
    color: 'unavailable',
    hex: CONGESTION_COLORS.unavailable,
    label: 'Unavailable',
    detail: 'Route geometry diverged — baseline time used',
  },
]

/**
 * Classify delay of ETA vs backend baseline.
 * Low requires both % and absolute to be low; higher tiers take the worse of either.
 *
 * @param {number|null} baselineSec backend time
 * @param {number|null} etaSec Mapbox / hybrid traffic ETA
 * @returns {{ kind: string, color: string, label: string, delaySec: number, delayPct: number }}
 */
export function classifyDelayVsBaseline(baselineSec, etaSec) {
  if (
    baselineSec == null ||
    etaSec == null ||
    Number.isNaN(baselineSec) ||
    Number.isNaN(etaSec)
  ) {
    return {
      kind: 'low',
      color: DELAY_KIND_META.low.color,
      label: DELAY_KIND_META.low.label,
      delaySec: 0,
      delayPct: 0,
    }
  }

  const delaySec = Math.max(0, etaSec - baselineSec)
  const delayPct = baselineSec > 0 ? (delaySec / baselineSec) * 100 : 0

  // Low: both thresholds must be low
  if (delaySec < 180 && delayPct < 10) {
    return {
      kind: 'low',
      color: DELAY_KIND_META.low.color,
      label: DELAY_KIND_META.low.label,
      delaySec,
      delayPct,
    }
  }

  let pctKind = 'low'
  if (delayPct > 60) {
    pctKind = 'severe'
  } else if (delayPct >= 30) {
    pctKind = 'heavy'
  } else if (delayPct >= 10) {
    pctKind = 'moderate'
  }

  let absKind = 'low'
  if (delaySec > 20 * 60) {
    absKind = 'severe'
  } else if (delaySec >= 10 * 60) {
    absKind = 'heavy'
  } else if (delaySec >= 3 * 60) {
    absKind = 'moderate'
  }

  const kind =
    DELAY_KIND_RANK[pctKind] >= DELAY_KIND_RANK[absKind] ? pctKind : absKind
  const meta = DELAY_KIND_META[kind]

  return {
    kind,
    color: meta.color,
    label: meta.label,
    delaySec,
    delayPct,
  }
}

/**
 * Color for delay of traffic duration vs a baseline (backend or free-flow).
 */
export function colorForTrafficDelay(baselineSec, trafficSec) {
  return classifyDelayVsBaseline(baselineSec, trafficSec).color
}

/**
 * @deprecated Prefer assessTrafficSituation / classifyDelayVsBaseline.
 */
export function colorForEtaDelta(valhallaSec, trafficSec) {
  return colorForTrafficDelay(valhallaSec, trafficSec)
}

/**
 * Classify a single Mapbox annotation edge as a congestion-like level using
 * predicted travel time vs free-flow (maxspeed), not Mapbox's congestion enum.
 * Prefer % delay — absolute minute thresholds don't scale to short edges.
 *
 * @returns {'low'|'moderate'|'heavy'|'severe'|'unknown'}
 */
export function classifyPredictedCongestionLevel({
  distanceM = 0,
  durationSec = 0,
  maxspeed = null,
} = {}) {
  const mph = maxspeedToMph(maxspeed)
  if (!(distanceM > 0) || !(durationSec > 0) || mph == null || mph <= 5) {
    return 'unknown'
  }
  const freeFlowSec = distanceM / (mph / 2.23693629)
  if (!(freeFlowSec > 0)) {
    return 'unknown'
  }
  const delaySec = Math.max(0, durationSec - freeFlowSec)
  const delayPct = (delaySec / freeFlowSec) * 100
  // Ignore tiny noise on short edges
  if (delaySec < 1.5 && delayPct < 10) {
    return 'low'
  }
  if (delayPct > 60) {
    return 'severe'
  }
  if (delayPct >= 30) {
    return 'heavy'
  }
  if (delayPct >= 10) {
    return 'moderate'
  }
  return 'low'
}

export function colorForCongestion(level) {
  if (!level) {
    return CONGESTION_COLORS.unknown
  }
  const normalized = level === 'high' ? 'heavy' : level
  return CONGESTION_COLORS[normalized] || CONGESTION_COLORS.unknown
}

/** Prefer worse congestion when combining annotation samples. */
export function worstCongestionLevel(levels) {
  const rank = {
    severe: 5,
    heavy: 4,
    high: 4,
    moderate: 3,
    low: 2,
    unknown: 1,
  }
  let best = 'unknown'
  let bestRank = 0
  for (const raw of levels || []) {
    const level = raw === 'high' ? 'heavy' : raw || 'unknown'
    const value = rank[level] || 0
    if (value > bestRank) {
      bestRank = value
      best = level
    }
  }
  return best
}

function isCongestedLevel(level) {
  if (!level) {
    return false
  }
  return CONGESTED_LEVELS.has(level === 'high' ? 'high' : level)
}

/**
 * Estimate Mapbox free-flow time from distance + maxspeed (and low/unknown
 * segments that already look free-flowing).
 *
 * @param {{ distance?: number[], duration?: number[], congestion?: string[], maxspeed?: object[] }} annotations
 */
export function estimateFreeFlowFromAnnotations(annotations) {
  const distances = annotations?.distance || []
  const durations = annotations?.duration || []
  const congestions = annotations?.congestion || []
  const maxspeeds = annotations?.maxspeed || []

  let freeFlowSec = 0
  let trafficDurationSec = 0
  let totalDistanceM = 0
  let congestedDistanceM = 0
  let segmentsWithMaxspeed = 0
  let segments = 0

  const count = Math.max(distances.length, durations.length, congestions.length)
  for (let i = 0; i < count; i++) {
    const dist = distances[i] || 0
    const dur = typeof durations[i] === 'number' ? durations[i] : 0
    const level = congestions[i] || 'unknown'
    segments += 1
    totalDistanceM += dist
    trafficDurationSec += dur
    if (isCongestedLevel(level)) {
      congestedDistanceM += dist
    }

    const mph = maxspeedToMph(maxspeeds[i])
    if (mph != null && mph > 5 && dist > 0) {
      const mps = mph / 2.23693629
      freeFlowSec += dist / mps
      segmentsWithMaxspeed += 1
    } else if (!isCongestedLevel(level)) {
      // No posted limit; treat observed duration as free-flow when congestion is low/unknown
      freeFlowSec += dur
    } else if (dur > 0) {
      // Congested without maxspeed: approximate free-flow ~65% of congested duration
      freeFlowSec += dur * 0.65
    }
  }

  const delaySec = Math.max(0, trafficDurationSec - freeFlowSec)
  const congestedDistanceShare =
    totalDistanceM > 0 ? congestedDistanceM / totalDistanceM : 0

  return {
    freeFlowSec,
    trafficDurationSec,
    delaySec,
    totalDistanceM,
    congestedDistanceM,
    congestedDistanceShare,
    segmentsWithMaxspeed,
    segmentCount: segments,
  }
}

/**
 * Classify route traffic for the ETA badge using delay vs **backend** baseline.
 * Mapbox free-flow is still computed for display. Delay tiers are gated so a
 * pure map/speed-limit mismatch (no Mapbox traffic signal) stays Low.
 */
export function assessTrafficSituation({
  annotations = null,
  congestionCounts = null,
  etaSec = null,
  baselineSec = null,
} = {}) {
  const fromAnnotations = annotations
    ? estimateFreeFlowFromAnnotations(annotations)
    : null

  const counts =
    congestionCounts ||
    summarizeCongestion(annotations?.congestion || []).counts ||
    {}

  let moderateSegments = 0
  let heavyOrSevereSegments = 0
  for (const [level, count] of Object.entries(counts)) {
    if (level === 'moderate') {
      moderateSegments += count
    }
    if (level === 'heavy' || level === 'severe' || level === 'high') {
      heavyOrSevereSegments += count
    }
  }

  const freeFlowSec = fromAnnotations?.freeFlowSec ?? null
  const mapboxTrafficSec = fromAnnotations?.trafficDurationSec ?? etaSec ?? null
  const displayEtaSec = etaSec ?? mapboxTrafficSec

  const classified = classifyDelayVsBaseline(baselineSec, displayEtaSec)
  const congestedShare = fromAnnotations?.congestedDistanceShare ?? 0
  const mapboxFreeFlowDelaySec = fromAnnotations?.delaySec ?? 0

  const baselineDelta = buildTimeDelta(baselineSec, displayEtaSec)
  const trafficDelay = buildTimeDelta(baselineSec, displayEtaSec)

  const mapboxVsFreeFlowPct =
    freeFlowSec > 0 ? (mapboxFreeFlowDelaySec / freeFlowSec) * 100 : 0
  const mapboxIndicatesTraffic =
    mapboxFreeFlowDelaySec > 30 && mapboxVsFreeFlowPct > 10
  const congestionIndicatesTraffic =
    moderateSegments > 0 || heavyOrSevereSegments > 0 || congestedShare >= 0.05

  // Gate: without a Mapbox traffic signal, treat elevated delay as map/speed mismatch
  let { kind, color, label } = classified
  if (
    kind !== 'low' &&
    !mapboxIndicatesTraffic &&
    !congestionIndicatesTraffic
  ) {
    kind = 'low'
    color = DELAY_KIND_META.low.color
    label = DELAY_KIND_META.low.label
  }

  return {
    kind,
    color,
    label,
    etaSec: displayEtaSec,
    baselineSec,
    freeFlowSec,
    trafficDelaySec: classified.delaySec,
    delayPct: classified.delayPct,
    delayKindRaw: classified.kind,
    mapboxFreeFlowDelaySec,
    congestedDistanceShare: congestedShare,
    baselineDelta,
    trafficDelay,
    freeFlow: fromAnnotations,
    mapboxTrafficSec,
    mapboxNoTrafficSec: freeFlowSec,
  }
}

/**
 * Color a maneuver from its congestion counts (not Valhalla delta).
 */
export function colorForManeuverTraffic(
  congestionCounts,
  valhallaSec,
  trafficSec
) {
  const assessment = assessTrafficSituation({
    congestionCounts,
    etaSec: trafficSec,
    baselineSec: valhallaSec,
  })
  return assessment.color
}

/**
 * Allocate Map Matching edge durations onto Valhalla maneuvers using densify shape indices.
 * annotations.duration[i] is travel time between coordinate i and i+1.
 *
 * @param {Array<{ begin_shape_index: number, end_shape_index: number, time?: number, length?: number }>} maneuvers
 * @param {Array<{ shapeIndex: number }>} densifyPoints
 * @param {{ duration?: number[], distance?: number[], congestion?: string[] }} annotations
 */
export function allocateAnnotationsToManeuvers(
  maneuvers,
  densifyPoints,
  annotations
) {
  const durations = annotations?.duration || []
  const distances = annotations?.distance || []
  const congestions = annotations?.congestion || []

  return (maneuvers || []).map((mnv) => {
    const begin = mnv.begin_shape_index
    const end = mnv.end_shape_index
    let trafficTime = 0
    let trafficDistanceM = 0
    const congestionCounts = {}

    const densifyEdgeCount = Math.max(densifyPoints.length - 1, 0)
    for (let i = 0; i < densifyEdgeCount; i++) {
      const edgeStart = densifyPoints[i].shapeIndex
      const edgeEnd = densifyPoints[i + 1].shapeIndex
      // edge overlaps maneuver if intervals overlap
      if (edgeEnd <= begin || edgeStart >= end) {
        continue
      }
      // Annotations follow matched geometry (often ≠ densify edges when only
      // endpoints are Map Matching waypoints). Stretch by densify progress.
      let annIdx = i
      if (durations.length > 0 && durations.length !== densifyEdgeCount) {
        annIdx = Math.min(
          durations.length - 1,
          Math.floor((i / densifyEdgeCount) * durations.length)
        )
      }
      const edgeDuration = durations[annIdx]
      const dist = distances[annIdx]
      if (typeof edgeDuration === 'number') {
        trafficTime += edgeDuration
      }
      if (typeof dist === 'number') {
        trafficDistanceM += dist
      }
      const congestionLevel = congestions[annIdx]
      if (congestionLevel) {
        congestionCounts[congestionLevel] =
          (congestionCounts[congestionLevel] || 0) + 1
      }
    }

    const valhallaTime = mnv.time ?? null
    const delta = buildTimeDelta(valhallaTime, trafficTime || null)
    return {
      instruction: mnv.instruction,
      begin_shape_index: begin,
      end_shape_index: end,
      valhalla: {
        time: valhallaTime,
        length: mnv.length ?? null,
      },
      traffic: {
        time: trafficTime || null,
        lengthM: trafficDistanceM || null,
        congestionCounts,
      },
      delta,
      color: colorForManeuverTraffic(
        congestionCounts,
        valhallaTime,
        trafficTime || null
      ),
    }
  })
}

/**
 * Summarize congestion annotation array.
 * @param {string[]} congestion
 */
export function summarizeCongestion(congestion) {
  const counts = {}
  for (const level of congestion || []) {
    counts[level] = (counts[level] || 0) + 1
  }
  let dominant = 'unknown'
  let max = 0
  for (const [level, count] of Object.entries(counts)) {
    if (count > max) {
      max = count
      dominant = level
    }
  }
  return { counts, dominant }
}

/**
 * Build map-ready congestion-colored segments.
 * Prefers 1:1 alignment between annotation[i] and geometry segment i→i+1.
 * Falls back to densify/trace coordinates when those match annotation length.
 *
 * @param {GeoJSON.LineString|null} geometry
 * @param {string[]} congestion
 * @param {{ fallbackCoordinates?: number[][] }} [options] [lon,lat][]
 * @returns {Array<{ coordinates: number[][], color: string, congestion: string }>}
 */
export function buildCongestionSegments(geometry, congestion, options = {}) {
  const levels = Array.isArray(congestion) ? congestion : []
  const overviewCoords = geometry?.coordinates
  const fallback = options.fallbackCoordinates

  let coords = null
  if (
    Array.isArray(overviewCoords) &&
    overviewCoords.length >= 2 &&
    (levels.length === 0 || overviewCoords.length === levels.length + 1)
  ) {
    coords = overviewCoords
  } else if (
    Array.isArray(fallback) &&
    fallback.length >= 2 &&
    (levels.length === 0 || fallback.length === levels.length + 1)
  ) {
    coords = fallback
  } else if (Array.isArray(overviewCoords) && overviewCoords.length >= 2) {
    coords = overviewCoords
  } else if (Array.isArray(fallback) && fallback.length >= 2) {
    coords = fallback
  }

  if (!coords || coords.length < 2) {
    return []
  }

  const segmentCount = coords.length - 1
  const rawSegments = []
  for (let i = 0; i < segmentCount; i++) {
    let level = 'unknown'
    if (levels.length > 0) {
      if (levels.length === segmentCount) {
        level = levels[i] || 'unknown'
      } else {
        // Stretch annotations along geometry when lengths differ
        const annotationIdx = Math.min(
          levels.length - 1,
          Math.floor((i / segmentCount) * levels.length)
        )
        level = levels[annotationIdx] || 'unknown'
      }
    }
    const normalized = level === 'high' ? 'heavy' : level
    rawSegments.push({
      coordinates: [coords[i], coords[i + 1]],
      color: colorForCongestion(normalized),
      congestion: normalized,
    })
  }

  // Merge consecutive same-congestion segments for cleaner polylines
  return mergeCongestionSegments(rawSegments)
}

function mergeCongestionSegments(segments) {
  if (!segments.length) {
    return []
  }
  const merged = []
  let current = {
    coordinates: [...segments[0].coordinates],
    color: segments[0].color,
    congestion: segments[0].congestion,
  }
  for (let i = 1; i < segments.length; i++) {
    const next = segments[i]
    if (next.congestion === current.congestion) {
      current.coordinates.push(next.coordinates[1])
    } else {
      merged.push(current)
      current = {
        coordinates: [...next.coordinates],
        color: next.color,
        congestion: next.congestion,
      }
    }
  }
  merged.push(current)
  return merged
}

export function compareLegTotals(
  valhallaLeg,
  trafficDurationSec,
  trafficDistanceM
) {
  const valhallaTime = valhallaLeg?.summary?.time ?? null
  // Valhalla length is in trip units (miles for this app); keep as-is for display
  const valhallaLength = valhallaLeg?.summary?.length ?? null
  const delta = buildTimeDelta(valhallaTime, trafficDurationSec)
  const lengthDeltaM =
    trafficDistanceM != null && valhallaLength != null
      ? trafficDistanceM - valhallaLength * 1609.344
      : null
  const lengthPct =
    lengthDeltaM != null && valhallaLength
      ? (lengthDeltaM / (valhallaLength * 1609.344)) * 100
      : null

  return {
    valhalla: { time: valhallaTime, length: valhallaLength },
    traffic: {
      time: trafficDurationSec,
      lengthM: trafficDistanceM,
      lengthMiles:
        trafficDistanceM != null ? trafficDistanceM / 1609.344 : null,
    },
    delta: {
      ...delta,
      lengthM: lengthDeltaM,
      lengthPct,
    },
    color: colorForEtaDelta(valhallaTime, trafficDurationSec),
  }
}
